"use client";
import React from "react";
import Head from "next/head";
import { TurnrGrid } from "~/components/TurnrGrid.jsx";
import { fetchBagShipments, fetchMarkBagShipment, fetchShipmentLabel } from "~/services";
import { formatOrderItemName, getQRUrl } from "~/utils";
import { useTableSortQuery } from "~/components/table/useTableSortQuery.jsx";
import Link from "next/link";
import { QRCodeSVG } from "qrcode.react";
import { PrintLabelComponent } from "~/components/Print.jsx";
import { useTablePageState, useTableSizeState } from "~/hooks";
import { getReturnId } from "~/cores/orderCore";
import { COLORS } from "rylak-common/src/constants";
import { QUERY_MAP } from "~/constants";
import { getTableHeadDateTime } from "~/components/table/cells/TableCellDateTime.jsx";
import TurnrTableSmart, { TurnrTableCell, TurnrTableRow } from "~/components/table/TurnrTableSmart.jsx";
import { TurnrCard, TurnrCardContent } from "~/components/card/TurnrCard.jsx";
import TurnrModal, { TurnrModalContainer, TurnrModalContent, TurnrModalTitle } from "~/components/modals/TurnrModal.jsx";
import { CustomLinearProgress } from "~/components/loading/Progress.jsx";
import { TurnrButton } from "~/components/buttons/TurnrButton";
import { faCheck, faPrint } from "@fortawesome/pro-regular-svg-icons";
import TurnrCheckbox from "~/components/input/TurnrCheckbox";
import useSelectListItemModalState from "~/components/modals/useSelectListItemModalState.jsx";
import { formatReadableDateAndTime, serviceIsLoading, useServiceHook } from "rylak-common";
import { useRunCallbackIfValueDeeplyChanged } from "rylak-common/src/hooks/useRunCallbackIfValueDeeplyChanged.jsx";
import { useQueryState } from "rylak-common/src/hooks/useQueryState.jsx";
import { useAppProps } from "~/app/_apps/AppPropsContext";
import { getTurnrShipmentHref } from "~/cores/pageAccessCore";

let TABLE_HEAD_DATA = {
    items: [
        {
            id: "id",
            label: "ID",
            defaultSortDirection: "desc",
            defaultSortedBy: true,
        },
        {
            id: "trackingReference",
            label: "Tracking Reference",
            defaultSortDirection: "desc",
        },
        {
            id: "bags",
            label: "Nr of Bags",
        },
        getTableHeadDateTime({ label: "Time" }),
        {
            id: "print",
            sortable: false,
        },
    ],
};

function PrintModal({ appProps, shipment, open, onClose }) {
    let { serviceData: markAsDoneService, fetchServiceData: fetchMarkAsDoneFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        onReceive: () => {
            appProps.reloadPage();
        },
        fetchFn: fetchMarkBagShipment,
    });

    const bagShipment = shipment?.bagShipment;
    const bagShipmentId = bagShipment?.id;

    let { serviceData: shipmentLabelService } = useServiceHook({
        appProps,
        fetchRequested: true,
        fetchData: { bagShipmentId },
        fetchFn: fetchShipmentLabel,
    });

    return (
        <TurnrModal open={open} onClose={onClose}>
            <TurnrModalContainer>
                <TurnrModalTitle>Print</TurnrModalTitle>
                {serviceIsLoading(shipmentLabelService) && <CustomLinearProgress />}
                <TurnrModalContent>
                    <PrintLabelComponent
                        shipmentLabelService={shipmentLabelService}
                        printEnabled={shipmentLabelService.data}
                        pages={getPrintPages({
                            shipment,
                            appProps,
                        })}
                    />
                    <TurnrGrid container>
                        <TurnrGrid item xs={6}>
                            Shipment Id:
                        </TurnrGrid>
                        <TurnrGrid item xs={6}>
                            <b>{bagShipmentId}</b>
                        </TurnrGrid>
                        <TurnrGrid item xs={6}>
                            Tracking Reference:
                        </TurnrGrid>
                        <TurnrGrid item xs={6}>
                            <b style={{ whiteSpace: "nowrap" }}>{bagShipment?.trackingReference}</b>
                        </TurnrGrid>
                    </TurnrGrid>
                    <p id="simple-modal-description">
                        <TurnrButton
                            color={COLORS.SUCCESS}
                            fullWidth
                            size="large"
                            disabled={markAsDoneService.fetching || serviceIsLoading(shipmentLabelService)}
                            fontAwesomeIcon={faCheck}
                            onClick={() => {
                                fetchMarkAsDoneFn({
                                    bagShipmentId: bagShipmentId,
                                });
                            }}
                        >
                            Mark as done
                        </TurnrButton>
                    </p>
                </TurnrModalContent>
            </TurnrModalContainer>
        </TurnrModal>
    );
}

function getPrintPages({ shipment, appProps }) {
    return shipment?.bags.map(({ c2bShipment, orderItem }, index) => {
        const order = c2bShipment?.order;
        return (
            <div key={index} className={"p3"}>
                <TurnrGrid container justifyContent="center" alignItems="center" alignContent="center">
                    <TurnrGrid container item fullWidth xs={12} spacing={2} wrap="nowrap">
                        <TurnrGrid item>
                            <QRCodeSVG
                                size={130}
                                value={getQRUrl({
                                    order,
                                    initialProductId: orderItem.id,
                                    appProps,
                                    returnId: getReturnId({ orderItem }),
                                })}
                            />
                        </TurnrGrid>
                        <TurnrGrid item>
                            <h2
                                style={{
                                    marginTop: -2,
                                    fontWeight: "bold",
                                    marginBottom: "10px",
                                    fontSize: "24px",
                                }}
                            >
                                Bag {index + 1}
                            </h2>
                            <h2
                                style={{
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                }}
                            >
                                Använd din mobils kamera för att skanna QR koden och följ sedan instruktionerna.
                            </h2>
                            <p style={{ fontSize: "10px" }}>Om du inte har möjlighet att skanna QR koden så lägg ner rätt plagg i påsen och ställ ut för paketupphämtning.</p>
                        </TurnrGrid>
                    </TurnrGrid>
                    <TurnrGrid
                        container
                        item
                        justifyContent="center"
                        alignItems="center"
                        alignContent="center"
                        style={{
                            height: "200px",
                            overflow: "hidden",
                            textAlign: "center",
                        }}
                    >
                        <TurnrGrid item>
                            <div style={{ fontSize: "30px" }}>{formatOrderItemName(orderItem)}</div>
                        </TurnrGrid>
                    </TurnrGrid>
                    <TurnrGrid container item spacing={0} direction="column" alignContent="center">
                        <TurnrGrid item justifyContent={"center"}>
                            <h1
                                style={{
                                    marginTop: 0,
                                    fontWeight: "bold",
                                    textAlign: "center",
                                    marginBottom: "20px",
                                    fontSize: "60px",
                                }}
                            >
                                {c2bShipment?.trackingReference?.substring(0, 9)}
                            </h1>
                        </TurnrGrid>
                    </TurnrGrid>
                </TurnrGrid>
            </div>
        );
    });
}

export default function BagShipmentsPage() {
    const { appProps } = useAppProps();
    const { selectedLogisticsProvider } = appProps;

    const [page, setPage] = useTablePageState();
    const [size] = useTableSizeState();

    const [showMarkedAsDone, setShowMarkedAsDone] = useQueryState({
        key: QUERY_MAP.MARKED_AS_DONE,
    });

    let tableSortData = useTableSortQuery(TABLE_HEAD_DATA);
    let { sort } = tableSortData;
    let { serviceData: shipmentsService, fetchServiceData: fetchShipmentsFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: fetchBagShipments,
    });

    useRunCallbackIfValueDeeplyChanged([showMarkedAsDone, selectedLogisticsProvider, page, size, sort], () => {
        fetchShipmentsFn(
            {
                includeBackofficeHandled: !!showMarkedAsDone,
                page,
                size,
                sort,
            },
            { silentFetch: true },
        );
    });

    const [printModalOpen, selectedShipment, setSelectedShipment] = useSelectListItemModalState(false);

    return (
        <>
            <Head>
                <title>Shipments</title>
            </Head>
            {selectedShipment && <PrintModal open={printModalOpen} key={selectedShipment} onClose={setSelectedShipment} appProps={appProps} shipment={selectedShipment} />}
            <TurnrCard>
                <TurnrCardContent>
                    <TurnrGrid container spacing={3} alignItems={"center"}>
                        <TurnrGrid item>
                            <TurnrCheckbox
                                checked={!!showMarkedAsDone}
                                onChange={(checked) => {
                                    setPage(0);
                                    setShowMarkedAsDone(checked || undefined);
                                }}
                                name="showMarkedAsDone"
                                label="Show marked as done"
                            />
                        </TurnrGrid>
                    </TurnrGrid>
                </TurnrCardContent>

                <TurnrTableSmart tableHeadData={TABLE_HEAD_DATA} tableSortData={tableSortData} serviceData={shipmentsService} page={page} setPage={setPage} pageSize={size}>
                    {shipmentsService?.data?.content?.map((shipment) => {
                        const { bagShipment, bags } = shipment;
                        return (
                            <TurnrTableRow href={getTurnrShipmentHref({ shipment: bagShipment })} key={bagShipment.id}>
                                <TurnrTableCell>
                                    <Link href={getTurnrShipmentHref({ shipment: bagShipment })}>{bagShipment?.id}</Link>
                                </TurnrTableCell>

                                <TurnrTableCell>{bagShipment?.trackingReference}</TurnrTableCell>
                                <TurnrTableCell>{bags.length}</TurnrTableCell>
                                <TurnrTableCell>{formatReadableDateAndTime({ dateTime: bagShipment?.createdAt })}</TurnrTableCell>
                                <TurnrTableCell width={"100%"}>
                                    <TurnrButton
                                        fullWidth
                                        size="large"
                                        variant={bagShipment.backofficeHandled ? "outlined" : "contained"}
                                        fontAwesomeIcon={bagShipment.backofficeHandled ? faCheck : faPrint}
                                        onClick={() => {
                                            setSelectedShipment(shipment);
                                        }}
                                    >
                                        {bagShipment.backofficeHandled ? "Print again" : "Print"}
                                    </TurnrButton>
                                </TurnrTableCell>
                            </TurnrTableRow>
                        );
                    })}
                </TurnrTableSmart>
            </TurnrCard>
        </>
    );
}
